import { apiFetchJson, HttpError } from "@/services/apiClient";
import { API_URL } from "@/lib/utils";


export const CCH_PIPELINE_TRIGGERS = ["raw", "without_raw"] as const;
export type CchPipelineTriggerKind = (typeof CCH_PIPELINE_TRIGGERS)[number];

export const CCH_TRIGGER_LABEL: Record<CchPipelineTriggerKind, string> = {
  raw: "CCH raw",
  without_raw: "CCH without raw",
};

export const CCH_TRIGGER_DESCRIPTION: Record<CchPipelineTriggerKind, string> = {
  raw: "Re-pulls the raw CCH extract into the lakehouse, then runs the downstream processing chain. The full load.",
  without_raw:
    "Skips the raw extract and only runs the downstream processing against the already-loaded raw tables. Faster, and the right choice when the raw is known-current.",
};

/** Fabric job-instance state, normalised by the API. */
export type CchTriggerStatus =
  | "queued"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "unknown";

export function isCchRunInFlight(status: CchTriggerStatus | undefined): boolean {
  return status === "queued" || status === "running";
}

export interface CchPipelineTriggerPayload {
  trigger: CchPipelineTriggerKind;
}

export interface CchPipelineRun {
  run_id: string;
  trigger: CchPipelineTriggerKind;
  status: CchTriggerStatus;
  failure_reason: string | null;
  fabric_job_instance_id: string | null;
  triggered_by: string | null;
  triggered_at: string;
  started_at: string | null;
  finished_at: string | null;
  last_polled_at: string | null;
  poll_error: string | null;
}

export const CCH_PIPELINE_RUNS_QUERY_KEY = ["cch", "pipeline", "runs"] as const;

export const cchPipelineRunQueryKey = (runId: string) =>
  ["cch", "pipeline", "run", runId] as const;

export const CCH_HISTORY_PAGE_SIZES = [10, 25, 50, 100] as const;
export type CchHistoryPageSize = (typeof CCH_HISTORY_PAGE_SIZES)[number];
export const cchPipelineHistoryQueryKey = (params: {
  limit: number;
  skip: number;
  excludeInFlight: boolean;
}) =>
  [
    "cch",
    "pipeline",
    "runs",
    "page",
    params.limit,
    params.skip,
    params.excludeInFlight,
  ] as const;

export class CchPipelineConflictError extends Error {
  runId: string | null;
  reason: string;
  constructor(message: string, reason: string, runId: string | null) {
    super(message);
    this.name = "CchPipelineConflictError";
    this.reason = reason;
    this.runId = runId;
  }
}

async function triggerOnce(
  payload: CchPipelineTriggerPayload,
): Promise<CchPipelineRun> {
  try {
    return await apiFetchJson<CchPipelineRun>(
      `${API_URL}/integrations/cch/pipeline/runs`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  } catch (err) {
    if (err instanceof HttpError && err.status === 409) {
      const match = err.message.match(/\(([^)]+)\)/);
      const detailMatch = err.message.match(/HTTP 409:\s*(.+)/);
      const detail = detailMatch?.[1]?.trim() ?? err.message;
      throw new CchPipelineConflictError(
        detail,
        "already_running",
        match?.[1] ?? null,
      );
    }
    throw err;
  }
}

export function triggerCchPipeline(
  payload: CchPipelineTriggerPayload,
): Promise<CchPipelineRun> {
  return triggerOnce(payload);
}

export interface CchPipelineRunsPage {
  items: CchPipelineRun[];
  total: number;
  limit: number;
  skip: number;
}

export interface ListCchPipelineRunsOptions {
  limit?: number;
  skip?: number;
  trigger?: CchPipelineTriggerKind;
  excludeInFlight?: boolean;
}

export function listCchPipelineRuns(
  options: ListCchPipelineRunsOptions = {},
): Promise<CchPipelineRunsPage> {
  const params = new URLSearchParams();
  params.set("limit", String(options.limit ?? 10));
  if (options.skip && options.skip > 0) params.set("skip", String(options.skip));
  if (options.trigger) params.set("trigger", options.trigger);
  if (options.excludeInFlight) params.set("exclude_in_flight", "true");
  return apiFetchJson<CchPipelineRunsPage>(
    `${API_URL}/integrations/cch/pipeline/runs?${params.toString()}`,
  );
}

export function getCchPipelineRun(runId: string): Promise<CchPipelineRun> {
  return apiFetchJson<CchPipelineRun>(
    `${API_URL}/integrations/cch/pipeline/runs/${encodeURIComponent(runId)}`,
  );
}

export function cancelCchPipelineRun(runId: string): Promise<CchPipelineRun> {
  return apiFetchJson<CchPipelineRun>(
    `${API_URL}/integrations/cch/pipeline/runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST" },
  );
}
